import type { QueryClient } from '@tanstack/react-query';

import { productsQueryKeyPrefix } from '@/queries/products';
import { USER_QUERY_KEY } from '@/queries/users';
import store from '@/redux/store';
import { clearGAUserId } from '@/shared/ga';
import { getTrackingId, resetMP } from '@/shared/mp';
import { clearPostAuthDestination } from '@/shared/postAuthDestination';
import { isSameQueryKey, isTrackingIdentityStale, writeSessionWatermark } from '@/shared/sessionWatermark';

/**
 * Drop everything on this origin that belonged to the previous user, then record the new owner.
 *
 * The single cleanup for both ways the owner can change — the session ending, and it becoming
 * somebody else's.
 *
 * ## Called from exactly one place
 *
 * `UserSessionSync`, on the `signed-out` and `switched` transitions. `useLogout` deliberately does
 * *not* call it: writing `null` into the session query wakes the observer, which classifies the
 * answer and runs this. Everything a logout needs to forget is therefore decided here, once,
 * whether the session ended by a button on this origin or quietly in another app.
 *
 * Centralized rather than registered by the features that own the data: a lazily-loaded feature
 * that never mounted would never register, and its *persisted* data outlives the component, so it
 * would silently survive. The inverse holds for in-memory state — an unmounted component holds
 * nothing stale — which is why `TourSync`'s sync baseline keys off the user id in place instead of
 * being reached into from here.
 *
 * Redux is not touched here. `unsetUserData()` is dispatched by the caller, and the slices that own
 * user-scoped state (`tours`, `visitedLinks`, `sponsorPlaque`) already reset on it, synchronously.
 */
export const forgetPreviousUser = (queryClient: QueryClient, nextUserId: string | null) => {
  // Before the cache drop, so a refetch triggered by it can never be mistaken for a second
  // transition by an observer that re-reads the watermark.
  writeSessionWatermark(nextUserId);

  /**
   * Signing out is a statement of being done here — *unless* the Speedy Flow is running, in which
   * case it is a step within a journey that is continuing. Abandoning a half-made account is not
   * abandoning where the visitor was trying to get to, and they are about to make another account
   * to get there.
   *
   * Asked of the flow rather than passed in by whoever triggered the logout, so that this stays the
   * only place that decides. `GuestOnlyGuard` already suppresses its own navigation the same way
   * and for the same reason: an auth transition *inside* the flow is not the transition the rest of
   * the app reacts to.
   */
  if (!store.getState().modals.speedyFlow?.visible) {
    clearPostAuthDestination();
  }

  /**
   * Most of what this app caches varies by user: notifications, album media, owned products,
   * retention offers, habitat access and favourites, and `products` — whose `eligibility.allowed`
   * is the API's per-user answer to whether this visitor may buy.
   *
   * The session query is the one exception, because both this and `UserSessionSync` are driven by
   * it: dropping it would destroy the answer that triggered the cleanup, forcing a refetch and
   * briefly returning the app to an unresolved session. It already holds the correct new value by
   * the time we get here.
   */
  queryClient.removeQueries({
    predicate: ({ queryKey }) => !isSameQueryKey(queryKey, USER_QUERY_KEY),
  });

  /**
   * The analytics identity, which this app used to leave entirely alone.
   *
   * That was the plan's one real gap: it reasoned that because Mixpanel persists to a cookie on
   * `.zoolife.tv`, whichever app noticed the transition could reset it for everybody. Persistence
   * reads that cookie only at init and rewrites the whole thing from memory on every save, so an app
   * that never resets keeps the departed user in memory and writes them straight back — and a logout
   * pressed here, followed by signed-out browsing here, reported as the person who left.
   *
   * Guarded, because the reset is destructive in the other direction: it rotates the anonymous id, and
   * doing that twice for one logout orphans whatever was collected under the first one. See
   * `isTrackingIdentityStale` for the three cases.
   */
  if (isTrackingIdentityStale(getTrackingId(), nextUserId)) {
    resetMP();
  }

  // Unguarded, unlike the reset above: GA's user id configures this page's tracker rather than a
  // shared cookie, so it is always ours to clear.
  clearGAUserId();
};

/**
 * Re-ask for anything that was answered *for a signed-out caller*, now that there is a session.
 *
 * The other half of the ownership change, for the transition where nothing is owed a Forget.
 * Signing in does not make the cache somebody else's — it is the same visitor throughout — but it
 * does change who the API thinks is asking, and `/v2/products` answers that question differently:
 * it resolves every price in the caller's currency, which is the IP's for a visitor and
 * `user.stripe.currency` for a returning customer, and it judges `eligibility` against an account
 * it did not have. A returning customer who bills in CAD therefore signs in to USD cards, and the
 * Speedy Flow carries a price id off one of them into a checkout the API then bills in CAD — the
 * number on screen and the number charged disagree.
 *
 * Invalidated rather than removed, unlike the Forget: these are the queries the page in front of
 * the visitor is rendering from, and dropping them would blank the cards mid-flow instead of
 * correcting the price under them.
 *
 * Not needed on a switch: the Forget drops the whole cache there, so the price list refetches for
 * the new account on its own.
 */
export const refreshPricesForNewSession = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: productsQueryKeyPrefix });
};

export default forgetPreviousUser;
